import { z } from "zod";
import type { Actor } from "./platform";

const classes = ["Undecided", "Druid", "Hunter", "Mage", "Paladin", "Priest", "Rogue", "Shaman", "Warlock", "Warrior"] as const;
export const introductionSpecsByClass = {
  Undecided: [],
  Druid: ["Balance", "Feral", "Restoration"],
  Hunter: ["Beast Mastery", "Marksmanship", "Survival"],
  Mage: ["Arcane", "Fire", "Frost"],
  Paladin: ["Holy", "Protection", "Retribution"],
  Priest: ["Discipline", "Holy", "Shadow"],
  Rogue: ["Assassination", "Combat", "Subtlety"],
  Shaman: ["Elemental", "Enhancement", "Restoration"],
  Warlock: ["Affliction", "Demonology", "Destruction"],
  Warrior: ["Arms", "Fury", "Protection"],
} as const satisfies Record<typeof classes[number], readonly string[]>;
const specs = ["Undecided", ...new Set(Object.values(introductionSpecsByClass).flat())] as [string, ...string[]];
export function introductionSpecChoices(className: typeof classes[number]): readonly string[] {
  return ["Undecided", ...introductionSpecsByClass[className]];
}
export const introductionChoices = {
  className: classes,
  spec: specs,
  secondClassName: classes,
  secondSpec: specs,
  // Retained for drafts/profiles from the first pilot. No longer asked or displayed.
  role: ["Undecided", "Tank", "Healer", "Melee DPS", "Ranged DPS", "Flexible", "Social only"],
  raidStyle: ["Undecided", "Casual", "Semi-hardcore", "Hardcore", "Social only"],
  casualTeam: ["Undecided", "Yes", "Yes, if moving teams later is possible", "I prefer a progression-focused team", "I'd like to discuss it"],
  leadership: ["Undecided", "Yes", "Open to learning", "Maybe", "No"],
} as const;
const answer = (max: number) => z.string().trim().max(max);
export const introductionAnswersSchema = z.object({
  plannedMain: answer(80).default(""),
  className: z.enum(introductionChoices.className).default("Undecided"),
  spec: z.enum(introductionChoices.spec).default("Undecided"),
  secondClassName: z.enum(introductionChoices.secondClassName).default("Undecided"),
  secondSpec: z.enum(introductionChoices.secondSpec).default("Undecided"),
  role: z.enum(introductionChoices.role).default("Undecided"),
  raidStyle: z.enum(introductionChoices.raidStyle).default("Undecided"),
  casualTeam: z.enum(introductionChoices.casualTeam).default("Undecided"),
  availability: answer(400).default(""),
  timezone: answer(80).default(""),
  experience: answer(1000).default(""),
  logs: answer(700).default(""),
  leadership: z.enum(introductionChoices.leadership).default("Undecided"),
  leadershipExperience: answer(700).default(""),
  goals: answer(500).default(""),
}).strict().superRefine((answers, context) => {
  for (const [classKey, specKey] of [["className", "spec"], ["secondClassName", "secondSpec"]] as const) {
    if (!introductionSpecChoices(answers[classKey]).includes(answers[specKey])) context.addIssue({ code: "custom", path: [specKey], message: "Choose a spec belonging to the selected class." });
  }
});
export type IntroductionAnswers = z.infer<typeof introductionAnswersSchema>;
export const emptyIntroduction = (): IntroductionAnswers => introductionAnswersSchema.parse({});
export const introductionSchema = z.object({
  version: z.literal(1),
  guildId: z.string().regex(/^\d{15,22}$/),
  userId: z.string().regex(/^\d{15,22}$/),
  displayName: z.string().max(100),
  test: z.boolean(),
  revision: z.number().int().positive(),
  status: z.enum(["submitted", "withdrawn"]),
  submittedAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  answers: introductionAnswersSchema,
}).strict();
export type MemberIntroduction = z.infer<typeof introductionSchema>;
export const introductionFields: { key: keyof IntroductionAnswers; label: string }[] = [
  { key: "plannedMain", label: "Planned main" }, { key: "className", label: "First-choice class" },
  { key: "spec", label: "First-choice spec" }, { key: "secondClassName", label: "Second-choice class" },
  { key: "secondSpec", label: "Second-choice spec" }, { key: "raidStyle", label: "Raid style" },
  { key: "casualTeam", label: "Open to a more casual raid team" },
  { key: "availability", label: "Availability" }, { key: "timezone", label: "Time zone" },
  { key: "experience", label: "WoW experience" }, { key: "logs", label: "Historical characters / logs" },
  { key: "leadership", label: "Interested in leading" }, { key: "leadershipExperience", label: "Leadership experience" },
  { key: "goals", label: "Looking for in the guild" },
];
export function canBrowseIntroductions(actor: Actor): boolean {
  return actor.officeAccess && actor.capabilities.includes("approve");
}
export function introductionSearchText(profile: MemberIntroduction): string {
  return [profile.displayName, profile.userId, ...introductionFields.map(({ key }) => profile.answers[key])].join("\n").toLocaleLowerCase("en");
}
/** Only explicitly supplied Warcraft Logs URLs become clickable. Never fetch member text. */
export function introductionLogLinks(value: string): string[] {
  return [...new Set((value.match(/https:\/\/[^\s<>]+/g) ?? []).filter(raw => {
    try {
      const url = new URL(raw);
      return !url.username && !url.password && !url.port && (url.hostname === "warcraftlogs.com" || url.hostname.endsWith(".warcraftlogs.com"));
    } catch { return false; }
  }))].slice(0, 5);
}
